import React, { useEffect, useState } from 'react';
import Header from '../components/header';
import store, { useAppDispatch, useAppSelector } from '../store';
import { Asset } from '../customTypings/branding/asset';
import { Button, Dialog, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import BrandingImageDialog from '../components/dialogs/branding-image-dialog';
import { Dimensions } from '../customTypings/branding/crop';
import { setActiveBrand, setActiveLanguage, setAllLanguages } from '../store/reducers/brandSlice';
import { setIsSearching } from '../store/reducers/cabinetSlice';
import { parse, stringify, CssRuleAST, CssDeclarationAST, CssStylesheetAST } from '@adobe/css-tools';
import { SketchPicker } from 'react-color';
import fonts from '../assets/fonts.json';
import { getBrandAssets, patchBrandAssets } from '../utilities/brand-api-utils';
import CircularProgress from '@mui/material/CircularProgress';

interface TextState {
  key: string,
  editing: boolean
}
interface StyleState {
  selector: string,
  editing: boolean
}

function SpinningLoader() {
  return (
    <div 
      style={{
        width: '100%',
        marginTop: '100px'
      }}>
      <CircularProgress color="inherit" />
    </div>
  );
}

function BrandingView() {
  const activeBrand = useAppSelector(store => store.brand.activeBrand);
  const activeLanguage = useAppSelector(store => store.brand.activeLanguage);
  const dispatch = useAppDispatch();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [images, setImages] = useState<Array<Asset>>([]);
  const [translationTable, setTranslationTable] = useState<Record<string, Record<string,Record<string,string>>>>({});
  const [translateFile, setTranslateFile] = useState<Asset>();
  const [paletteFile, setPaletteFile] = useState<Asset>({key: '', file: ''});
  const [paletteTable, setPaletteTable] = useState<Record<string, Record<string, string>>>({});
  const [styleFiles, setStyleFiles] = useState<Array<Asset>>([]);
  const [selectedImage, setSelectedImage] = useState<Asset>({key: '', file: ''});
  const [selectedImageDim, setSelectedImageDim] = useState<Dimensions>({width: 0, height: 0});
  const [languages, setLanguages] = useState<string[]>([]);
  const [cssParsedFile, setCssParsedFile] = useState<CssStylesheetAST>();
  const [cssRulesTemp, setCssRulesTemp] = useState<Array<CssRuleAST>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [textSectionState, setTextSectionState] = useState<Array<TextState>>([{key: '', editing: false}]);
  const [styleSectionState, setStyleSectionState] = useState<Array<StyleState>>([{selector: '', editing: false}]);
  const [paletteSectionState, setPaletteSectionState] = useState<Array<TextState>>([{key: '', editing: false}]);
  
  dispatch(setIsSearching(false));
  interface TError {
    header: string,
    fieldHeader: string,
    flag: string,
    error: boolean
  }
  const tError: TError[] = [
    {header: 'order-view', fieldHeader: 'locker-string', flag: '{lockers}', error: false},
    {header: 'order-view', fieldHeader: 'info', flag: '{info}', error: false},
    {header: 'pickup-code-entry', fieldHeader: 'default-instructions', flag: '{digits}', error: false},
    {header: 'pickup-code-entry', fieldHeader: 'grubhub-instructions', flag: '{digits}', error: false},
    {header: 'pickup-code-entry', fieldHeader: 'ubereats-instructions', flag: '{digits}', error: false},
    {header: 'popups', fieldHeader: 'has_expired', flag: '{code}', error: false},
    {header: 'popups', fieldHeader: 'unknown_locker_type', flag: '{lockerType}', error: false},
    {header: 'create-order', fieldHeader: 'default-instructions', flag: '{digits}', error: false},
    {header: 'create-order', fieldHeader: 'grubhub-instructions', flag: '{digits}', error: false},
    {header: 'create-order', fieldHeader: 'ubereats-instructions', flag: '{digits}', error: false},
    {header: 'manage-orders', fieldHeader: 'ready-timer-text', flag: '{timeString}', error: false},
    {header: 'manage-orders', fieldHeader: 'expire-timer-text', flag: '{timeString}', error: false},
  ];
  const [textError, setTextError] = useState<TError[]>(tError);
  
  const tabs = [
    'Images', 
    'Styling',
    'Palette',
    'Text'];
  const [activeTab, setActiveTab] = useState<string>(tabs[0]);
    
  const onTabChanged = (tab: string) => {
    setActiveTab(tab);
  };

  const editImage = (image: Asset) => {
    const i = new Image();
    i.src = image.file;
    setSelectedImageDim({width: i.width, height: i.height});
    setSelectedImage(image);
    setDialog(true);
  };

  const refreshAssets = () => {
    const brand = activeBrand;
    setRefresh(!refresh);
    dispatch(setActiveBrand(brand));
  };

  const editTextSectionState = (key: string, editing: boolean) => {
    const oldArr = textSectionState;
    const newArr = oldArr.map(x => {
      return x.key === key ? {key: key, editing: editing} : x;
    });
    setTextSectionState(newArr);
  };

  const editStyleSectionState = (selector: string, editing: boolean) => {
    const oldArr = styleSectionState;
    const newArr = oldArr.map(x => {
      return x.selector === selector ? {selector: selector, editing: editing} : x;
    });
    setStyleSectionState(newArr);
  };

  const textValueChanged = (header: string, fieldHeader: string, value: string) => {
    const temp: any = translationTable;

    temp[header][fieldHeader] = value;
    
    setTranslationTable((prev) => ({
      ...prev,
      ...temp
    }));
  };

  const onTextEditCancel = () => {
    if (translateFile) {
      setTranslationTable(JSON.parse(translateFile.file));
    }
  };

  const onTextEditSaved = async () => {
    if (translateFile) {
      const parsedTranslateFile = JSON.parse(translateFile.file);
      parsedTranslateFile[activeLanguage] = translationTable;
      const payload = {"key": translateFile.key, "file": JSON.stringify(parsedTranslateFile)};
      await patchBrandAssets(store.getState().authorization.cloudKey, activeBrand, payload);
      setRefresh(!refresh);
    }
  };

  const styleValueChanged = (textField: boolean, selector: string, property: string, value: string) => {
    const temp = cssParsedFile;
    (temp?.stylesheet.rules as CssRuleAST[]).map(x => {
      if (x.selectors[0] === selector) {
        x.declarations.map(p => {
          const dec = (p as CssDeclarationAST);
          if (dec.property === property) {
            if (dec.property === 'border' && value.includes('#') && !textField) {
              const colorIndex = dec.value.indexOf('#');
              const first = dec.value.substring(0, colorIndex);
              const last = dec.value.substring(colorIndex + 6, dec.value.length -1);

              dec.value = first + value + last;
            } else {
              dec.value = value;
            }
          }
        });
      }
    });
    setCssRulesTemp([...(temp?.stylesheet.rules as CssRuleAST[])]);
    if (temp) {
      setCssParsedFile((prev) => ({
        ...prev,
        ...temp
      }));
    }
  };

  const onStyleEditCancel = () => {
    setCssRulesTemp([...parse(styleFiles[0].file).stylesheet.rules as CssRuleAST[]]);
    setCssParsedFile((prev) => ({
      ...prev,
      ...parse(styleFiles[0].file)
    }));
  };
  
  const onStyleEditSaved = async () => {
    if (cssParsedFile) {
      const payload = {"key": styleFiles[0].key, "file": stringify(cssParsedFile)};
      await patchBrandAssets(store.getState().authorization.cloudKey, activeBrand, payload);
      setRefresh(!refresh);
    }
  };

  const paletteValueChanged = (header: string, fieldHeader: string, value: string) => {
    const temp = paletteTable;
    temp[header][fieldHeader] = value;
    
    setPaletteTable((prev) => ({
      ...prev,
      ...temp
    }));
  };

  const editPaletteSectionState = (key: string, editing: boolean) => {
    const oldArr = paletteSectionState;
    const newArr = oldArr.map(x => {
      return x.key === key ? {key: key, editing: editing} : x;
    });
    setPaletteSectionState(newArr);
  };

  const onPaletteEditCancel = () => {
    if (paletteFile) {
      setPaletteTable(JSON.parse(paletteFile.file));
    }
  };

  const onPaletteEditSaved = async () => {
    if (paletteFile) {
      const payload = {"key": paletteFile.key, "file": JSON.stringify(paletteTable)};
      await patchBrandAssets(store.getState().authorization.cloudKey, activeBrand, payload);
      setRefresh(!refresh);
    }
  };

  const checkText = (header: string, fieldHeader: string, value: string) => {
    const errObj = textError.find(x => x.header === header && x.fieldHeader === fieldHeader);
    if (errObj) {
      if (!value.includes(errObj.flag)) {
        errObj.error = true;
        return true;
      } else {
        errObj.error = false;
        return false;
      }
    }
  };

  useEffect(() => {
    const getBrandFiles = async() => {
      setIsLoading(true);

      await getBrandAssets(store.getState().authorization.cloudKey, activeBrand).then(
        (brandAssets) => {
        setIsLoading(false);
        const brandFiles = brandAssets.data;
        interface Assets {
          img: Asset[],
          json: Asset[],
          style: Asset[] 
        }

        if (brandFiles) {
          const assets: Assets= {
            img: [],
            json: [],
            style: [] 
          };

          brandFiles.forEach((allAssets: Array<Asset>) => {
            allAssets.forEach((oneAsset: Asset) => {
              if (oneAsset.key) {
                const splitPath = oneAsset.key.split('.');
                const keyType = splitPath[splitPath.length - 1];
                switch (keyType) {
                  case 'jpeg':
                  case 'jpg':
                  case 'png':
                    assets.img.push({key: oneAsset.key, file: oneAsset.file});
                    break;
                  case 'json':
                    assets.json.push({key: oneAsset.key, file: oneAsset.file});
                    break;
                  case 'css':
                    assets.style.push({key: oneAsset.key, file: oneAsset.file});
                    break;
                }
              }
            });
          });

          // IMAGES
          const imgFiles = assets.img;
          setImages(imgFiles);

          // CSS
          const cssFiles = assets.style;
          const styleHexOnly: CssRuleAST[] = [];
          const parsedCss: CssStylesheetAST = parse(cssFiles[0].file);
          
          // REPLACE COLORS WITH HEX
          parsedCss.stylesheet.rules.map((rule) => {
            const r = (rule as CssRuleAST);
            r.declarations.map((dec) => {
              const d = (dec as CssDeclarationAST);
              if (d.value.includes('black')) {
                d.value = d.value.replace('black', '#000000');
              } else if (d.value.includes('white')) {
                d.value = d.value.replace('white', '#FFFFFF');
              } else if (d.value.includes('transparent')) {
                d.value = d.value.replace('transparent', '#FFFFFF00');
              }

              // REPLACE #XXX WITH #XXXXXX
              if (d.value.split(' ').find(x => {
                if (x.includes('#')) {
                  return x;
                }
              })?.split(' ')[0].length === 4) {
                const colorIndex = d.value.indexOf('#');
                const first = d.value.substring(0, colorIndex);
                const color = (d.value.substring(colorIndex, colorIndex+4) + d.value.substring(colorIndex+1, colorIndex+4));
                const last = d.value.substring(colorIndex+4, d.value.length-1);
                d.value = first + color + last;
              }
            });
            styleHexOnly.push(r);
          });
          parsedCss.stylesheet.rules = styleHexOnly;
          cssFiles[0].file = stringify(parsedCss);
          setStyleFiles(cssFiles);

          // PARSE JSON FILES INTO TEXT AND PALLETTE
          const jsonFiles = assets.json;
          const obj = [...parse(cssFiles[0].file).stylesheet.rules as CssRuleAST[]];
          
          setCssRulesTemp(obj);
          setCssParsedFile(parse(cssFiles[0].file));
          
          const styleState = obj.map(x => {
            return {selector: x.selectors[0], editing: false};
          });
          setStyleSectionState(styleState);

          // TEXT
          const translateJsonFile = jsonFiles.find(x => x.key.split('/')[2] === 'translate.json');

          if (translateJsonFile) {
            setTranslateFile(translateJsonFile);

            const parsedTranslate = JSON.parse(translateJsonFile.file);
            setLanguages(Object.keys(JSON.parse(translateJsonFile.file)));
            dispatch(setAllLanguages(Object.keys(JSON.parse(translateJsonFile.file))));

            // Grab the first language in the translate file as the "active" language for the brand
            // In the future we can set the preferred language for a brand, and display that as the default language on load.
            const activeLanguage = Object.keys(JSON.parse(translateJsonFile.file)).sort().shift() as keyof typeof parsedTranslate;
            setTranslationTable(parsedTranslate[activeLanguage]);
            dispatch(setActiveLanguage(activeLanguage as string));
          }
          // PALETTE
          const paletteFileTemp = jsonFiles.find(x => x.key.split('/')[2] === 'palette.json');
          if (paletteFileTemp) {
            const paletteState = Object.keys(JSON.parse(paletteFileTemp.file)).map((key) => {
              return {key: key, editing: false};
            });

            const parsedPaletteFileTemp = JSON.parse(paletteFileTemp.file);
            Object.entries(parsedPaletteFileTemp).map(([header, value]) => {
              Object.entries(value as Record<string, string>).map(([fieldHeader, value2]) => {
                
                // REPLACE COLORS WITH HEX
                if (value2.includes('black')) {
                  parsedPaletteFileTemp[header][fieldHeader] = '#000000';
                } else if (value2.includes('white')) {
                  parsedPaletteFileTemp[header][fieldHeader] = '#FFFFFF';
                } else if (value2.includes('transparent')) {
                  parsedPaletteFileTemp[header][fieldHeader] = '#FFFFFF00';
                } 
                
                // REPLACE #XXX WITH #XXXXXX
                if (value2.split(' ').find(x => {
                  if (x.includes('#')) {
                    return x;
                  }
                })?.split(' ')[0].length === 4) {
                  const colorIndex = value2.indexOf('#');
                  const first = value2.substring(0, colorIndex);
                  const color = (value2.substring(colorIndex, colorIndex+4) + value2.substring(colorIndex+1, colorIndex+4));
                  const last = value2.substring(colorIndex+4, value2.length-1);
                  parsedPaletteFileTemp[header][fieldHeader] = first + color + last;
                }
              });
            });

            setPaletteSectionState(paletteState);
            setPaletteFile({key: paletteFileTemp.key, file: JSON.stringify(parsedPaletteFileTemp)});
            setPaletteTable(parsedPaletteFileTemp);
          }
        }
      });
    };

    if (activeBrand) {
      getBrandFiles();
    }
  }, [activeBrand, refresh]);

  useEffect(() => {
    const parseTranslateFile = async () => {
      // Ensure that the translate file exists
      // Additional check that the translate file contains the active language
      // fixes a race condition wherein sometimes the translate file doesn't
      // fully load before trying to get its object keys
      if ((translateFile) && (JSON.parse(translateFile.file)[activeLanguage])) {
        const textState = Object.keys(JSON.parse(translateFile.file)[activeLanguage]).map((x) => {
          return {key: x, editing: false};
        });
        setTranslationTable(JSON.parse(translateFile.file)[activeLanguage]);
        setTextSectionState(textState);
      }
    };

    if (activeLanguage) {
      parseTranslateFile();
    }
  }, [translateFile, activeLanguage]);

  // useEffect(() => {
  //   setIsLoading(!isLoading);
  // }), [];

  return (
    <div>
      <Header title='Branding' headerTabs={tabs} isBranding={true} onTabChange={onTabChanged}/>
      {(() => {
        switch (activeTab) {
          case 'Images': 
            return (
              <div>
                {isLoading === true
                  ? <div 
                    style={{
                      width: '100%',
                      marginTop: '100px'
                    }}>
                    <CircularProgress color="inherit" />
                  </div>
                  : <div
                  style={{
                    width: '100%',
                    textAlign: 'left'
                  }}>
                  {images.map((image,index) => (
                    <div 
                      key={index} 
                      style={{
                        display: 'inline-block', 
                        backgroundColor: '#102941',
                        margin: '16px',
                        padding: '16px',
                        borderRadius: '10px'
                      }}>
                      <div
                        style={{
                          width: '300px',
                          height: '500px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Typography variant='h5' color='white'>{image.key.split('/')[2].split('.')[0]}</Typography>
                        <img
                          src={image.file} width='200px' style={{backgroundColor: 'transparent'}}/>
                        <Button
                          variant='contained'
                          sx={{
                            fontSize: '20px'
                          }}
                          onClick={() => editImage(image)}>
                            Change
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>}
              </div>
            );
          case 'Styling':
            return (
              <div style={{margin: '20px'}}>
                {isLoading === true
                  ? <div 
                    style={{
                      width: '100%',
                      marginTop: '100px'
                    }}>
                    <CircularProgress color="inherit" />
                  </div>
                  : (() => {
                  return (cssRulesTemp.map((x, index) => (
                    <div key={index} style={{textAlign: 'left'}}>
                      <div style={{textAlign: 'left', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography align='left' variant='h3'>{x.selectors[0]}</Typography>
                        {styleSectionState.find(y => y.selector === x.selectors[0])?.editing ? 
                          <div style={{display: 'flex', flexDirection: 'row'}}>
                            <Button 
                              variant='contained'
                              sx={{
                                backgroundColor: '#BDBDBD',
                                '&: hover': {
                                  backgroundColor: '#FE2E26'
                                },
                                marginRight: '10px'
                              }}
                              onClick={() => {
                                onStyleEditCancel();
                                editStyleSectionState(x.selectors[0], false);
                                }}>
                              Cancel
                            </Button>
                            <Tooltip title={ `Save changes made to "${x.selectors[0]}"`}>
                              <Button variant='contained' onClick={() => {
                                onStyleEditSaved();
                                editStyleSectionState(x.selectors[0], false);
                              }}>Save</Button> 
                            </Tooltip>
                          </div> :
                          <Tooltip title={`Edit "${x.selectors[0]}" Properties`}>
                            <Button variant='contained' onClick={() => editStyleSectionState(x.selectors[0], true)}>Edit</Button>
                          </Tooltip>
                        }
                      </div>
                      <hr />
                      {x.declarations.map((prop, index) => (
                        <div key={index} style={{display: 'inline-block', width: '350px', margin: '20px'}}>
                          <Stack direction='column' width='350px'>
                            <Typography align='left' variant='h5'>{(prop as CssDeclarationAST).property}</Typography>
                            <Stack direction='row' justifyContent='start'>
                              {(prop as CssDeclarationAST).property === 'font-family' ?
                              <Select
                                sx={{
                                  color: 'black', 
                                  width: '350px', 
                                  height: 'auto',
                                  marginRight: '74px',
                                }}
                                disabled={!styleSectionState.find(y => y.selector === x.selectors[0])?.editing}
                                value={(prop as CssDeclarationAST).value.replace(' !important', '')}
                                onChange={(e) => styleValueChanged(false, x.selectors[0], (prop as CssDeclarationAST).property, `${e.target.value} !important`)}>
                                {fonts.map((font, index) => (
                                  <MenuItem 
                                    key={index}
                                    value={font}>
                                    {font}
                                  </MenuItem>
                                ))}
                              </Select> :
                              <TextField
                                onChange={(e) => styleValueChanged(
                                  true,
                                  x.selectors[0], 
                                  (prop as CssDeclarationAST).property, 
                                  e.target.value
                                )} 
                                inputProps={{ maxLength: 24 }}
                                disabled={!styleSectionState.find(y => y.selector === x.selectors[0])?.editing} 
                                value={(prop as CssDeclarationAST).value} />
                              }
                              {((prop as CssDeclarationAST).value.includes('#') ||
                                (prop as CssDeclarationAST).property.includes('color') ||
                                (prop as CssDeclarationAST).property.includes('background')) && styleSectionState.find(y => y.selector === x.selectors[0])?.editing ?
                              <SketchPicker color={
                                  (prop as CssDeclarationAST).property === 'border' ?
                                  (prop as CssDeclarationAST).value.split('#')[1].split(' ')[0] :
                                  (prop as CssDeclarationAST).value
                                } 
                                onChange={(hex) => styleValueChanged(
                                  false,
                                  x.selectors[0], 
                                  (prop as CssDeclarationAST).property, 
                                  hex.hex
                                )}/>
                              : <></>}
                            </Stack>
                          </Stack>
                        </div>
                      ))}
                    </div>
                  )));
                })()}
              </div>
              );
          case 'Palette':
            return (
              isLoading === true
                ? <div 
                  style={{
                    width: '100%',
                    marginTop: '100px'
                  }}>
                  <CircularProgress color="inherit" />
                </div>
                : <div style={{margin: '20px', textAlign: 'left'}}>
                  {Object.entries(paletteTable).map(([header, value]) => (
                    <div key={header}>
                      <div style={{textAlign: 'left', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography align='left' variant='h3'>{header}</Typography>
                        {paletteSectionState.find(x => x.key === header)?.editing ? 
                        <div style={{display: 'flex', flexDirection: 'row'}}>
                          <Button 
                            variant='contained'
                            sx={{
                              backgroundColor: '#BDBDBD',
                              '&: hover': {
                                backgroundColor: '#FE2E26'
                              },
                              marginRight: '10px'
                            }}
                            onClick={() => {
                              onPaletteEditCancel();
                              editPaletteSectionState(header, false);
                            }}>
                            Cancel
                          </Button>
                          <Tooltip title={ `Save changes made to "${header}"`}>
                            <Button variant='contained' onClick={() => {
                              onPaletteEditSaved();
                              editPaletteSectionState(header, false);
                            }}>Save</Button> 
                          </Tooltip>
                        </div> :
                        <Tooltip title={`Edit "${header}" Properties`}>
                          <Button variant='contained' onClick={() => editPaletteSectionState(header, true)}>Edit</Button>
                        </Tooltip>
                      }
                      </div>
                      <hr />
                      {Object.entries(value).map(([fieldHeader, value2]) => (
                        <div
                          style={{
                            display: 'inline-block',
                            width: '350px',
                            margin: '20px'
                          }} 
                          key={fieldHeader}>
                          <Stack direction='column' width='350px'>
                            <Typography align='left' variant='h5'>{fieldHeader}</Typography>
                            <Stack direction='row' justifyContent='start'>
                              <TextField
                                inputProps={{ maxLength: 12 }}
                                onChange={(e) => paletteValueChanged(header, fieldHeader, e.target.value)} 
                                disabled={!paletteSectionState.find(x => x.key === header)?.editing} 
                                value={JSON.stringify(value2).split('"')[1]}/>
                              {paletteSectionState.find(x => x.key === header)?.editing ?
                              <SketchPicker color={JSON.stringify(value2).split('"')[1]}
                                onChange={(hex) => paletteValueChanged(header, fieldHeader, hex.hex)}/>
                                : <></>
                              }
                            </Stack>
                          </Stack>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            );
          case 'Text':
            return (
              <div style={{margin: '20px'}}>
                {isLoading === true
                  ? <div 
                    style={{
                      width: '100%',
                      marginTop: '100px'
                    }}>
                    <CircularProgress color="inherit" />
                  </div>
                  : Object.entries(translationTable).map(([header, value]) => (
                  <div key={header}>
                    <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                      <Typography align='left' variant='h3'>{header}</Typography>
                      {textSectionState.find(x => x.key === header)?.editing ? 
                        <div style={{display: 'flex', flexDirection: 'row'}}>
                          
                          <Button 
                            variant='contained'
                            sx={{
                              backgroundColor: '#BDBDBD',
                              '&: hover': {
                                backgroundColor: '#FE2E26'
                              },
                              marginRight: '10px'
                            }}
                            onClick={() => {
                              onTextEditCancel();
                              editTextSectionState(header, false);
                            }}>
                            Cancel
                          </Button>
                          <Tooltip title={ `Save changes made to "${header}"`}>
                            <Button 
                              variant='contained'
                              disabled={textError.filter(x => x.header === header && x.error === true).length > 0}
                              onClick={() => {
                              onTextEditSaved();
                              editTextSectionState(header, false);
                            }}>Save</Button>
                          </Tooltip> 
                        </div> :
                        <Tooltip title={`Edit "${header}" Properties`}>
                          <Button variant='contained' onClick={() => editTextSectionState(header, true)}>Edit</Button>
                        </Tooltip>
                      }
                    </div>
                    <hr />
                    <div
                      style={{
                        height: 'auto',
                        textAlign: 'left',
                      }}>
                      {Object.entries(value).map(([fieldHeader, value2]) => (
                        <div
                          style={{
                            display: 'inline-block',
                            width: '400px',
                            margin: '20px'
                          }} 
                          key={fieldHeader}>
                          <Typography align='left' variant='h5'>{fieldHeader}</Typography>
                          <TextField
                            inputProps={{ maxLength: 120 }}
                            onChange={(e) => textValueChanged(header, fieldHeader, e.target.value)} 
                            disabled={!textSectionState.find(x => x.key === header)?.editing} 
                            sx={{width: '100%'}}
                            value={JSON.stringify(value2).split('"')[1]}
                            error={checkText(header, fieldHeader, JSON.stringify(value2).split('"')[1])}
                            helperText={
                              checkText(header, fieldHeader, JSON.stringify(value2).split('"')[1]) ?
                              `Message must include ${textError.find(x => x.header === header && x.fieldHeader === fieldHeader)?.flag}` :
                              ''
                            }/>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
        }})()}
      <Dialog
        sx={{
          width: '100%'
        }}
        fullWidth
        maxWidth='lg'
        open={dialog}
        onClose={() => setDialog(false)}>
        <BrandingImageDialog 
          imageName={selectedImage.key.split('/')[2]} 
          image={selectedImage} 
          imageDim={selectedImageDim} 
          setDialog={setDialog} 
          refresh={refreshAssets} />
      </Dialog>
    </div>
  );
}

export default BrandingView;
